import { useCallback, useSyncExternalStore } from "react";
import { coordinatorRegistry } from "./registry";

/**
 * Returns the optimistic state from the coordinator for the given entityId,
 * or undefined if no coordinator is active (caller should fall back to RTK Query).
 *
 * Reactively subscribes to both registry changes (coordinator added/removed)
 * and coordinator state changes (enqueue, save complete, etc.).
 */
export function useEntityState<TState>(
  entityId: string,
): TState | undefined {
  const coordinator = useSyncExternalStore(
    coordinatorRegistry.subscribe,
    useCallback(() => coordinatorRegistry.get(entityId), [entityId]),
  );

  const optimisticState = useSyncExternalStore(
    useCallback(
      (cb: () => void) =>
        coordinator ? coordinator.subscribe(cb) : () => { /* no coordinator to subscribe to */ },
      [coordinator],
    ),
    useCallback(
      () => (coordinator ? coordinator.getOptimisticState() : undefined),
      [coordinator],
    ),
  );

  return optimisticState as TState | undefined;
}
